/**
 * config/markets.js
 * ─────────────────────────────────────────────────────────────────────────────
 * DASHBOARD MARKET REGISTRY — the single source of truth for per-country display
 * configuration on the admin dashboard (React SPA). This is the frontend twin of
 * the backend registry at `Lusuku_Dashboard_Node-Server/config/markets.js`; the
 * values that overlap (currency, currencyLocale, dialCode, geocodeCountry,
 * timezone, siteOrigin) MUST stay in sync with it.
 *
 * PRINCIPLE (multi_national branch): country is a data-access boundary. Adding a
 * new market (KE, RW, …) is an entry here plus its seed configs, NOT an
 * `if (country === 'XX')` scattered through the UI.
 *
 * INVARIANTS
 *   • Country codes are ISO 3166-1 alpha-2 UPPERCASE: 'UG', 'ZM', … Never a
 *     display name ("Uganda"), a dial code, or a currency.
 *   • DEFAULT_COUNTRY is 'UG'. Every helper falls back to it so the live Uganda
 *     path renders identically when no country is supplied (legacy sessions).
 *   • This module is dependency-free (pure data + pure helpers) so it can be
 *     imported from the axios layer, context, components, and tests without side
 *     effects.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export const DEFAULT_COUNTRY = "UG";

/**
 * @typedef {Object} Market
 * @property {string} country        ISO-2 code (matches the map key)
 * @property {string} name           Human display name (for UI copy)
 * @property {string} currency       ISO-4217 currency code (UGX, ZMW, …)
 * @property {string} currencyLocale BCP-47 locale for Intl.NumberFormat
 * @property {string} dialCode       E.164 country calling code, digits only
 * @property {string} cca2           lowercase alpha-2 (phone pickers / flags)
 * @property {string} geocodeCountry Google `components=country:<code>` value
 * @property {string} timezone       IANA tz (surge/hours math)
 * @property {string} siteOrigin     canonical public web origin
 * @property {string} flagUrl        local SVG flag asset for consistent cross-platform rendering
 * @property {{lat:number,lng:number}} mapCenter  autocomplete/map bias center
 */

/** @type {Record<string, Market>} */
export const MARKETS = Object.freeze({
  // ── Uganda (LIVE — do not change these values) ─────────────────────────────
  UG: Object.freeze({
    country: "UG",
    name: "Uganda",
    currency: "UGX",
    currencyLocale: "en-UG",
    dialCode: "256",
    cca2: "ug",
    geocodeCountry: "ug",
    timezone: "Africa/Kampala", // UTC+3
    siteOrigin: "https://lusuku.ug",
    flagUrl: "/flags/ug.svg",
    mapCenter: Object.freeze({ lat: 0.3476, lng: 32.5825 }), // Kampala
  }),

  // ── Zambia (NEW — multi_national target market) ────────────────────────────
  ZM: Object.freeze({
    country: "ZM",
    name: "Zambia",
    currency: "ZMW",
    currencyLocale: "en-ZM",
    dialCode: "260",
    cca2: "zm",
    geocodeCountry: "zm",
    timezone: "Africa/Lusaka", // UTC+2
    siteOrigin: "https://lusuku.co.zm",
    flagUrl: "/flags/zm.svg",
    mapCenter: Object.freeze({ lat: -15.3875, lng: 28.3228 }), // Lusaka
  }),
});

/** All supported ISO-2 country codes, in registry order. */
export const SUPPORTED_COUNTRIES = Object.freeze(Object.keys(MARKETS));

/**
 * Normalize any country-ish input to a supported ISO-2 code, or null if it is
 * not a value we recognize. Accepts case-insensitive 'ug'/'UG'. Does NOT map
 * display names or dial codes — that is intentional (see invariants).
 * @param {*} input
 * @returns {string|null}
 */
export function normalizeCountry(input) {
  if (typeof input !== "string") return null;
  const code = input.trim().toUpperCase();
  return MARKETS[code] ? code : null;
}

/**
 * Get the full market config for a country, falling back to the DEFAULT market
 * ('UG') for any unknown/blank input so callers never get `undefined`. This is
 * the fail-safe that keeps the Uganda display path working when country is
 * absent (e.g. a legacy session whose profile predates the country field).
 * @param {*} country
 * @returns {Market}
 */
export function getMarket(country) {
  return MARKETS[normalizeCountry(country) || DEFAULT_COUNTRY];
}

/**
 * Format an amount in a market's currency using Intl.NumberFormat. Reads the
 * currency + locale from the registry so no view hardcodes "UGX "/"Shs "/
 * `Intl.NumberFormat("en-UG", …)`. Non-finite amounts render as an empty
 * currency string (e.g. "UGX 0") so a missing value never throws.
 * @param {number} amount
 * @param {*} country - ISO-2 code (fails safe to UG)
 * @returns {string}
 */
export function formatCurrency(amount, country) {
  const market = getMarket(country);
  const value = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  try {
    return new Intl.NumberFormat(market.currencyLocale, {
      style: "currency",
      currency: market.currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch (err) {
    // Defensive: a runtime that lacks the locale/currency should still render.
    return `${market.currency} ${value}`;
  }
}

const marketsRegistry = {
  DEFAULT_COUNTRY,
  MARKETS,
  SUPPORTED_COUNTRIES,
  normalizeCountry,
  getMarket,
  formatCurrency,
};

export default marketsRegistry;
